import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Calendar, User, Music, FileText, Loader2, ExternalLink } from 'lucide-react';
import { publicAPI } from '../services/api';
import { toast } from 'sonner';

const VerifyPage = () => {
  const { hash } = useParams();
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    verifyCertificate();
  }, [hash]);

  const verifyCertificate = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Verificando certificado:', hash);
      const response = await publicAPI.verifyCertificate(hash);
      
      console.log('✅ Certificado encontrado:', response.data);
      setCertificate(response.data);
      
    } catch (error) {
      console.error('❌ Erro ao verificar certificado:', error);
      
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Certificado não encontrado';
      
      if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(err => err.msg).join(', ');
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-8">
        <div className="max-w-2xl w-full bg-white rounded-xl p-12 text-center shadow-2xl">
          <Loader2 className="w-20 h-20 text-blue-600 mx-auto mb-6 animate-spin" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Verificando Certificado...</h1>
          <p className="text-gray-600">Aguarde enquanto validamos a autenticidade</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-8">
        <div className="max-w-2xl w-full bg-white rounded-xl p-12 text-center shadow-2xl border-2 border-red-200">
          <XCircle className="w-20 h-20 text-red-600 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Certificado Não Encontrado</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <Link to="/" className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all">
            Voltar para Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header de Sucesso */}
        <div className="bg-white rounded-xl p-8 text-center shadow-2xl mb-6 border-2 border-green-200">
          <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-2">✅ Certificado Válido</h1>
          <p className="text-gray-600 text-lg">Este certificado é autêntico e foi verificado com sucesso</p>
        </div>

        {/* Informações da Obra */}
        <div className="bg-white rounded-xl p-8 shadow-xl mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Music className="w-6 h-6 text-blue-600" />
            Informações da Obra
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-500 uppercase">Título</label>
                <p className="text-lg font-bold text-gray-900 mt-1">{certificate.work_title}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-500 uppercase">Tipo</label>
                <p className="text-lg text-gray-900 mt-1 capitalize">{certificate.work_type?.replace('_', ' ')}</p>
              </div>

              {certificate.genre && (
                <div>
                  <label className="text-sm font-semibold text-gray-500 uppercase">Gênero</label>
                  <p className="text-lg text-gray-900 mt-1">{certificate.genre}</p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-500 uppercase">Autor</label>
                <p className="text-lg text-gray-900 mt-1">{certificate.author_name}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-500 uppercase">Data de Registro</label>
                <p className="text-lg text-gray-900 mt-1">{formatDate(certificate.registered_at)}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-500 uppercase">ID da Obra</label>
                <p className="text-sm font-mono text-gray-600 mt-1 break-all">{certificate.work_id}</p>
              </div>
            </div>
          </div>

          {/* Coautores */}
          {certificate.coauthors && certificate.coauthors.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <label className="text-sm font-semibold text-gray-500 uppercase mb-3 block">Coautores</label>
              <div className="space-y-2">
                {certificate.coauthors.map((coauthor, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <span className="font-medium text-gray-900">{coauthor.name}</span>
                    <span className="text-blue-600 font-semibold">{coauthor.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Informações do Certificado */}
        <div className="bg-white rounded-xl p-8 shadow-xl mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Shield className="w-6 h-6 text-green-600" />
            Dados do Certificado
          </h2>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-gray-500 uppercase">Hash SHA-256</label>
              <p className="text-sm font-mono text-gray-900 mt-1 bg-gray-50 p-3 rounded-lg break-all">
                {certificate.certificate_hash}
              </p>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-500 uppercase">Data de Emissão</label>
              <p className="text-lg text-gray-900 mt-1">{formatDate(certificate.issued_at)}</p>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-500 uppercase">Timestamp</label>
              <p className="text-lg text-gray-900 mt-1">{formatDate(certificate.timestamp)}</p>
            </div>
          </div>
        </div>

        {/* Fundamentação Legal */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-8 shadow-xl mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Fundamentação Legal
          </h2>
          <div className="text-sm text-gray-700 space-y-2">
            <p>
              Este registro possui validade jurídica fundamentada na <strong>Lei nº 9.610/98 (Lei de Direitos Autorais)</strong>, 
              que protege os direitos do autor sobre sua obra intelectual.
            </p>
            <p>
              O certificado digital utiliza assinatura eletrônica e carimbo de tempo, conforme a <strong>Medida Provisória nº 2.200-2/2001 (ICP-Brasil)</strong>, 
              garantindo autenticidade, integridade e validade jurídica.
            </p>
            <p>
              A anterioridade da criação é comprovada pelo timestamp certificado, servindo como prova em disputas de autoria, 
              conforme os artigos 18 a 24 da Lei 9.610/98 e artigos 1.228 a 1.248 do Código Civil Brasileiro (propriedade intelectual).
            </p>
          </div>
        </div>

        {/* Ações */}
        <div className="flex gap-4 justify-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-lg"
          >
            <ExternalLink className="w-5 h-5" />
            Registrar Minha Obra
          </Link>
        </div>

        {/* Aviso de Verificação */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>✅ Esta verificação foi realizada em tempo real</p>
          <p className="mt-2">
            <strong>Link de Verificação:</strong> 
            <code className="ml-2 bg-gray-100 px-2 py-1 rounded">
              {window.location.href}
            </code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyPage;
